import React from "react";
import { Dropdown } from "react-bootstrap";
import { Trans, useTranslation } from "react-i18next";

const limitOptionsDefault = [5, 10, 15, 20, 25, 50, 100];

/**
 * Barre de pagination standard utilisée par toutes les pages de gestion
 * (mêmes comportements que la pagination construite pour Faculties.js).
 */
const DataTablePagination = ({
  page,
  limit,
  total,
  totalPages,
  onPageChange,
  onLimitChange,
  limitOptions = limitOptionsDefault,
  variant = "footer", // "header" (au-dessus du tableau) | "footer" (en dessous)
}) => {
  const { t } = useTranslation();
  const start = total === 0 ? 0 : (page - 1) * limit + 1;
  const end = Math.min(page * limit, total);

  if (variant === "header") {
    return (
      <div className="d-flex flex-wrap align-items-center justify-content-between px-3 py-2 border-bottom">
        <div className="d-flex align-items-center gap-3 flex-wrap">
          <span className="text-sm text-gray-600">
            <Trans i18nKey="common.pagination.showingEntries" values={{ start, end, total }} components={[<span className="font-semibold text-gray-900" />, <span className="font-semibold text-gray-900" />, <span className="font-semibold text-gray-900" />]} />
          </span>
          <span className="text-gray-300">|</span>
          <div className="d-flex align-items-center gap-2">
            <span className="text-sm text-gray-600">{t("common.pagination.rowsPerPage")}</span>
            <Dropdown className="drop-select">
              <Dropdown.Toggle
                as="div"
                className="drop-select-btn"
                style={{
                  minWidth: "70px",
                  background: "white",
                  border: "1px solid #e5e7eb",
                  borderRadius: "6px",
                  padding: "4px 12px",
                  cursor: "pointer",
                }}
              >
                {limit}
              </Dropdown.Toggle>
              <Dropdown.Menu>
                {limitOptions.map((option) => (
                  <Dropdown.Item
                    key={option}
                    onClick={() => onLimitChange(option)}
                    className={limit === option ? "active" : ""}
                  >
                    {option}
                  </Dropdown.Item>
                ))}
              </Dropdown.Menu>
            </Dropdown>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="d-flex flex-wrap align-items-center justify-content-between px-3 pt-3 border-top">
      <div className="text-sm text-gray-600">
        <Trans i18nKey="common.pagination.showingEntries" values={{ start, end, total }} components={[<span className="font-semibold text-gray-900" />, <span className="font-semibold text-gray-900" />, <span className="font-semibold text-gray-900" />]} />
      </div>

      {total > 0 && (
        <div className="flex items-center gap-1">
          <button
            className={`px-3 py-1 rounded-md border ${page === 1 ? "text-gray-400 cursor-not-allowed" : "text-gray-700 hover:bg-gray-100"}`}
            onClick={() => onPageChange(Math.max(1, page - 1))}
            disabled={page === 1}
          >
            <i className="fa-solid fa-angle-left"></i>
          </button>
          {[...Array(totalPages).keys()].map((n) => {
            const pageNum = n + 1;
            const totalPagesToShow = 5;
            const halfWay = Math.ceil(totalPagesToShow / 2);
            const startPage = Math.max(1, Math.min(page - halfWay + 1, totalPages - totalPagesToShow + 1));
            const endPage = Math.min(totalPages, startPage + totalPagesToShow - 1);

            if (pageNum === 1 || pageNum === totalPages || (pageNum >= startPage && pageNum <= endPage)) {
              return (
                <button
                  key={n}
                  className={`px-3 py-1 rounded-md border ${page === pageNum ? "bg-primary text-white" : "text-gray-700 hover:bg-gray-100"}`}
                  onClick={() => onPageChange(pageNum)}
                >
                  {pageNum}
                </button>
              );
            } else if (pageNum === startPage - 1 || pageNum === endPage + 1) {
              return (
                <span key={n} className="px-2 text-gray-400">
                  …
                </span>
              );
            }
            return null;
          })}
          <button
            className={`px-3 py-1 rounded-md border ${page === totalPages ? "text-gray-400 cursor-not-allowed" : "text-gray-700 hover:bg-gray-100"}`}
            onClick={() => onPageChange(Math.min(totalPages, page + 1))}
            disabled={page === totalPages}
          >
            <i className="fa-solid fa-angle-right"></i>
          </button>
        </div>
      )}
    </div>
  );
};

export default DataTablePagination;
